import { useState } from "react";
import { Check, X, CreditCard, ListChecks, BadgeCheck, Ban, Undo2, Link2, Mail, MessageCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail, getFileUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { academyPermissions } from "@/lib/academyAccess";
import { money, fmtDate, fmtDateTime, editionLabel, ENROLLMENT_STATUS, PAYMENT_METHOD_LABELS, selectCls, btnBrand, waLink } from "@/lib/academyFormat";
import PaymentModal from "@/components/academy/PaymentModal";
import PlanEditor, { cleanPlan } from "@/components/academy/PlanEditor";
import ReasonDialog from "@/components/academy/ReasonDialog";
import EnrollmentProgress from "@/components/academy/EnrollmentProgress";
import { ACADEMY } from "@/constants/testIds";

const PLAN_STATUS = { paid: "Pagada", pending: "Pendiente", overdue: "Vencida" };
const REFUND_STATUS = { pending: "Esperando aprobación", approved: "Aprobado", rejected: "Rechazado" };
// Fix W (2026-10-13): estado del email con el link personal
const INVITE_STATUS = { sent: "Email enviado", skipped: "Email sin configurar: mandale el link por WhatsApp", failed: "El email no salió" };

function Step({ done, label }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${done ? "bg-[#E3EDE1] text-[#5B7A54]" : "bg-[#E9F3F3] text-muted-foreground"}`}>
      {done ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />} {label}
    </span>
  );
}

// Fix U (2026-10-13): una inscripcion con su cuenta: total, pagado, saldo,
// plan de pagos y cada pago con su medio.
export default function EnrollmentCard({ enrollment: e, onChanged }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const mine = academyPermissions(user);
  const isAdmin = user?.role === "admin";
  const canPay = isAdmin || mine.includes("finance") || mine.includes("reception");
  const canMoney = isAdmin || mine.includes("finance");
  const canAcademic = isAdmin || mine.includes("academic");
  // Fix V (2026-10-13): reembolsos (solo fuerza mayor; los aprueba finanzas o la administradora)
  const canAskRefund = isAdmin || mine.some((p) => ["finance", "academic", "reception"].includes(p));
  // Fix W (2026-10-13): link personal del alumno (email, WhatsApp o copiar)
  const canInvite = isAdmin || mine.some((p) => ["executive", "academic", "reception"].includes(p));
  const [sending, setSending] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [refund, setRefund] = useState({ amount: "", reason: "", method: "card" });
  const [refundError, setRefundError] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [plan, setPlan] = useState([]);
  const [discount, setDiscount] = useState("");
  const [planError, setPlanError] = useState("");
  const acc = e.account || {};
  const st = ENROLLMENT_STATUS[e.status] || { label: e.status, cls: "" };
  const active = !["cancelled", "expired", "transferred"].includes(e.status);

  const openPlan = () => {
    setPlan((e.payment_plan || []).map((p) => ({ ...p })));
    setDiscount(String(e.discount || ""));
    setPlanError("");
    setPlanOpen(true);
  };

  const savePlan = async () => {
    try {
      await api.put(`/academy/enrollments/${e.id}`, { payment_plan: cleanPlan(plan), discount: Number(discount) || 0 });
      setPlanOpen(false);
      toast({ title: "Plan de pagos guardado" });
      onChanged();
    } catch (err) {
      setPlanError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const confirm = async () => {
    try {
      await api.post(`/academy/enrollments/${e.id}/confirm`);
      toast({ title: "Inscripción confirmada" });
      onChanged();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  // Si el servidor no tiene PUBLIC_APP_URL el link llega relativo: se completa con esta direccion.
  const fullLink = e.public_link && e.public_link.startsWith("/") ? `${window.location.origin}${e.public_link}` : e.public_link;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(fullLink);
      toast({ title: "Link copiado" });
    } catch {
      window.prompt("Copiá el link", fullLink);
    }
  };

  const resendInvite = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/academy/enrollments/${e.id}/send-invite`);
      toast({ title: INVITE_STATUS[data.status] || "Listo", description: data.status === "sent" ? undefined : data.error, variant: data.status === "sent" ? "default" : "destructive" });
      onChanged();
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const askRefund = async () => {
    setRefundError("");
    try {
      await api.post(`/academy/enrollments/${e.id}/refunds`, { amount: Number(refund.amount), reason: refund.reason.trim(), method: refund.method });
      setRefundOpen(false);
      toast({ title: "Reembolso pedido", description: "Lo tiene que aprobar finanzas o la administración." });
      onChanged();
    } catch (err) {
      setRefundError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const callWithReason = (url) => async (reason) => {
    try {
      await api.post(url, { reason });
      onChanged();
    } catch (err) {
      throw new Error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="bg-white border border-border rounded-xl p-5 space-y-4" data-testid={`${ACADEMY.enrollmentCardPrefix}${e.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-serif text-xl text-foreground">{e.course_name}</p>
          <p className="text-sm text-muted-foreground">{editionLabel(e.edition)}{e.edition?.location ? ` · ${e.edition.location}` : ""}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {e.price_label} {money(e.list_price)}{e.discount ? ` · descuento ${money(e.discount)}` : ""} · ejecutivo: {e.executive_name || "sin asignar"}
          </p>
        </div>
        <Badge className={`${st.cls} border-none font-normal`} data-testid={`${ACADEMY.enrollmentStatusPrefix}${e.id}`}>{st.label}</Badge>
      </div>

      <div className="flex flex-wrap gap-2">
        <Step done={e.steps?.reservation} label={`Reserva ${money(e.reservation_amount)}`} />
        <Step done={e.steps?.form} label="Formulario" />
        <Step done={e.steps?.contract} label="Contrato firmado" />
        {acc.fully_paid && <Step done label="Pagado al 100%" />}
      </div>

      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="rounded-lg bg-[#E9F3F3] p-3"><p className="text-xs text-muted-foreground">Total</p><p className="font-medium">{money(acc.total)}</p></div>
        <div className="rounded-lg bg-[#E9F3F3] p-3"><p className="text-xs text-muted-foreground">Pagado</p><p className="font-medium" data-testid={`${ACADEMY.enrollmentPaidPrefix}${e.id}`}>{money(acc.net_paid)}</p></div>
        <div className="rounded-lg bg-[#E9F3F3] p-3"><p className="text-xs text-muted-foreground">Saldo</p><p className={`font-medium ${acc.balance > 0 ? "text-[#A34B49]" : "text-[#5B7A54]"}`} data-testid={`${ACADEMY.enrollmentBalancePrefix}${e.id}`}>{money(acc.balance)}</p></div>
      </div>

      {/* Fix Y (2026-10-13): asistencia, practicas, inasistencia, cambio de fecha, saldo a favor y diploma */}
      <EnrollmentProgress enrollment={e} onChanged={onChanged} />

      {active && canInvite && e.public_link && (
        <div className="rounded-lg border border-border px-3 py-2 space-y-2" data-testid={`${ACADEMY.enrollmentLinkPrefix}${e.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">Página del alumno (pago, datos y contrato)</p>
            <div className="flex flex-wrap gap-1">
              <Button variant="ghost" size="sm" className="h-8" onClick={copyLink} data-testid={`${ACADEMY.enrollmentCopyLinkPrefix}${e.id}`}><Link2 className="w-4 h-4" /> Copiar link</Button>
              {waLink(e.student?.phone) && (
                <a href={waLink(e.student.phone, `Hola ${(e.student.name || "").split(" ")[0]}, este es tu link para completar la inscripción a ${e.course_name}: ${fullLink}`)} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-sm text-[#25A244] hover:bg-accent" data-testid={`${ACADEMY.enrollmentWhatsappLinkPrefix}${e.id}`}><MessageCircle className="w-4 h-4" /> WhatsApp</a>
              )}
              <Button variant="ghost" size="sm" className="h-8" onClick={resendInvite} disabled={sending} data-testid={`${ACADEMY.enrollmentResendInvitePrefix}${e.id}`}><Mail className="w-4 h-4" /> {sending ? "Enviando..." : "Reenviar email"}</Button>
            </div>
          </div>
          {e.invite_status && (
            <p className={`text-xs ${e.invite_status === "sent" ? "text-[#5B7A54]" : "text-[#8A6A2F]"}`} data-testid={`${ACADEMY.enrollmentInviteStatusPrefix}${e.id}`}>
              {INVITE_STATUS[e.invite_status] || e.invite_status}{e.invite_sent_at ? ` · ${fmtDateTime(e.invite_sent_at)}` : ""}{e.invite_status !== "sent" && e.invite_error ? ` — ${e.invite_error}` : ""}
            </p>
          )}
        </div>
      )}
      {e.contract?.pdf_url && (
        <a href={getFileUrl(e.contract.pdf_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-[#8A6A2F]" data-testid={`${ACADEMY.enrollmentContractPrefix}${e.id}`}>
          <FileText className="w-4 h-4" /> Contrato firmado por {e.contract.signer_name} ({fmtDateTime(e.contract_signed_at)})
        </a>
      )}

      {(acc.plan || []).length > 0 && (
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-1">Plan de pagos (después de la reserva)</p>
          <div className="divide-y divide-border border border-border rounded-lg">
            {acc.plan.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                <span>{fmtDate(p.due_date)}</span>
                <span>{money(p.amount)}</span>
                <span className={p.status === "overdue" ? "text-[#A34B49]" : p.status === "paid" ? "text-[#5B7A54]" : "text-muted-foreground"}>{PLAN_STATUS[p.status]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-1">Pagos</p>
        {(e.payments || []).length === 0 ? (
          <p className="text-sm text-muted-foreground">Todavía no hay pagos.</p>
        ) : (
          <div className="divide-y divide-border border border-border rounded-lg">
            {e.payments.map((p) => (
              <div key={p.id} className={`flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm ${p.status === "voided" ? "opacity-50 line-through" : ""}`}>
                <span>{fmtDate(p.paid_at)}</span>
                <span className="font-medium">{money(p.amount)}</span>
                <span>{PAYMENT_METHOD_LABELS[p.method] || p.method}{p.is_reservation ? " · reserva" : ""}</span>
                <span className="text-xs text-muted-foreground">{p.received_by_name}</span>
                {canMoney && p.status === "completed" && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={() => setVoidTarget(p)} data-testid={`${ACADEMY.paymentVoidPrefix}${p.id}`}>Anular</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {(e.refunds || []).length > 0 && (
        <div data-testid={`${ACADEMY.enrollmentRefundsPrefix}${e.id}`}>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground mb-1">Reembolsos</p>
          <div className="divide-y divide-border border border-border rounded-lg">
            {e.refunds.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                <span className="font-medium">{money(r.amount)}</span>
                <span className="text-muted-foreground">{r.reason}</span>
                <span className={r.status === "approved" ? "text-[#5B7A54]" : r.status === "rejected" ? "text-[#A34B49]" : "text-[#8A6A2F]"}>{REFUND_STATUS[r.status]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {active && (
        <div className="flex flex-wrap gap-2 pt-1">
          {canPay && <Button size="sm" className={btnBrand} onClick={() => setPayOpen(true)} data-testid={`${ACADEMY.enrollmentPayButtonPrefix}${e.id}`}><CreditCard className="w-4 h-4" /> Registrar pago</Button>}
          {canMoney && <Button size="sm" variant="outline" onClick={openPlan} data-testid={`${ACADEMY.enrollmentPlanButtonPrefix}${e.id}`}><ListChecks className="w-4 h-4" /> Plan y descuento</Button>}
          {canAcademic && e.status === "pending" && <Button size="sm" variant="outline" onClick={confirm} data-testid={`${ACADEMY.enrollmentConfirmButtonPrefix}${e.id}`}><BadgeCheck className="w-4 h-4" /> Confirmar a mano</Button>}
          {canAskRefund && acc.net_paid > 0 && <Button size="sm" variant="outline" onClick={() => { setRefund({ amount: "", reason: "", method: "card" }); setRefundError(""); setRefundOpen(true); }} data-testid={`${ACADEMY.enrollmentRefundButtonPrefix}${e.id}`}><Undo2 className="w-4 h-4" /> Pedir reembolso</Button>}
          {canAcademic && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setCancelOpen(true)} data-testid={`${ACADEMY.enrollmentCancelButtonPrefix}${e.id}`}><Ban className="w-4 h-4" /> Cancelar</Button>}
        </div>
      )}
      {e.status === "cancelled" && e.cancel_reason && <p className="text-xs text-muted-foreground">Cancelada: {e.cancel_reason}</p>}

      <PaymentModal open={payOpen} onOpenChange={setPayOpen} enrollment={e} onSaved={() => { toast({ title: "Pago registrado" }); onChanged(); }} />
      <ReasonDialog
        open={Boolean(voidTarget)} onOpenChange={(o) => !o && setVoidTarget(null)}
        title="Anular pago" description={voidTarget ? `${money(voidTarget.amount)} del ${fmtDate(voidTarget.paid_at)}. Queda registrado como anulado.` : ""}
        confirmLabel="Anular" onConfirm={callWithReason(`/academy/payments/${voidTarget?.id}/void`)}
      />
      <ReasonDialog
        open={cancelOpen} onOpenChange={setCancelOpen}
        title="Cancelar inscripción" description="Libera el lugar. Los pagos quedan registrados."
        confirmLabel="Cancelar inscripción" onConfirm={callWithReason(`/academy/enrollments/${e.id}/cancel`)}
      />
      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent className="max-w-md" data-testid={ACADEMY.refundModal}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Pedir reembolso</DialogTitle>
            <DialogDescription>Solo por fuerza mayor. Lo aprueba finanzas o la administración y queda anotado el motivo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Monto (USD)</Label>
                <Input type="number" min="0" step="0.01" value={refund.amount} onChange={(ev) => setRefund((r) => ({ ...r, amount: ev.target.value }))} data-testid={ACADEMY.refundAmountInput} />
              </div>
              <div>
                <Label className="text-sm text-muted-foreground mb-1 block">Cómo se devuelve</Label>
                <select className={selectCls} value={refund.method} onChange={(ev) => setRefund((r) => ({ ...r, method: ev.target.value }))}>
                  <option value="card">Tarjeta</option><option value="zelle">Zelle</option><option value="cash">Efectivo (de la caja)</option><option value="other">Otro</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Motivo</Label>
              <Input value={refund.reason} onChange={(ev) => setRefund((r) => ({ ...r, reason: ev.target.value }))} data-testid={ACADEMY.refundReasonInput} />
            </div>
            {refundError && <p className="text-sm text-destructive">{refundError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>Cancelar</Button>
            <Button className={btnBrand} onClick={askRefund} data-testid={ACADEMY.refundSaveButton}>Pedir reembolso</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={planOpen} onOpenChange={setPlanOpen}>
        <DialogContent className="max-w-lg" data-testid={ACADEMY.planModal}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Plan de pagos</DialogTitle>
            <DialogDescription>Precio {money(e.list_price)} ({e.price_label}). Las cuotas reparten lo que queda después de la reserva de {money(e.reservation_amount)}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-1 block">Descuento (USD)</Label>
              <Input type="number" min="0" step="0.01" value={discount} onChange={(ev) => setDiscount(ev.target.value)} data-testid={ACADEMY.planDiscountInput} />
            </div>
            <PlanEditor value={plan} onChange={setPlan} total={Math.max((e.list_price || 0) - (Number(discount) || 0) - (e.reservation_amount || 0), 0)} />
            {planError && <p className="text-sm text-destructive">{planError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanOpen(false)}>Cancelar</Button>
            <Button className={btnBrand} onClick={savePlan} data-testid={ACADEMY.planSaveButton}>Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
